/**
 * A file of places given as street addresses, with no latitude or longitude
 * (M4S-5904). PQL's Locations upload took addresses; here they are read into
 * rows for the geocoder (census-geocode.ts), each keeping its own radius or
 * media type like a coordinates file. PURE.
 */
import {
    columnIndex,
    RADIUS_HEADERS,
    radiusForType,
    TYPE_HEADERS,
    DEFAULT_RADIUS_METERS,
} from './placements';

const STREET = [
    'address',
    'street',
    'street address',
    'address 1',
    'address1',
    'address line 1',
    'addr',
];
const CITY = ['city', 'town', 'municipality'];
const STATE = ['state', 'st', 'state code', 'province', 'region'];
const ZIP = ['zip', 'zipcode', 'zip code', 'postal code', 'postal', 'postcode', 'zip5'];
const NAME = ['name', 'store', 'store name', 'location name', 'venue name', 'site', 'site name'];

/** One address row, as the geocoder takes it. */
export interface AddressRow {
    /** The spreadsheet row number (header is row 1), so a miss can be named. */
    row: number;
    street: string;
    city: string;
    state: string;
    zip: string;
    name?: string;
    radiusMeters: number;
    type?: string;
}

interface Columns {
    street: number;
    city: number;
    state: number;
    zip: number;
    name: number;
    type: number;
    radius: number;
}

/** The address columns of a header row: a street, and a zip or a city and state. Null when not an address header. */
export function addressColumns(header: string[]): Columns | null {
    const c: Columns = {
        street: columnIndex(header, STREET),
        city: columnIndex(header, CITY),
        state: columnIndex(header, STATE),
        zip: columnIndex(header, ZIP),
        name: columnIndex(header, NAME),
        type: columnIndex(header, TYPE_HEADERS),
        radius: columnIndex(header, RADIUS_HEADERS),
    };
    if (c.street < 0) return null;
    if (c.zip < 0 && (c.city < 0 || c.state < 0)) return null;
    return c;
}

function headerRow(rows: string[][]): number {
    for (let i = 0; i < Math.min(rows.length, 10); i++)
        if (addressColumns((rows[i] ?? []).map((x) => String(x ?? '')))) return i;
    return -1;
}

/** True when a file lists places by street address. */
export function isAddressTable(rows: string[][]): boolean {
    return headerRow(rows) >= 0;
}

/** The address rows, with the rows that cannot be geocoded named. */
export function addressRows(
    rows: string[][],
    defaultRadius = DEFAULT_RADIUS_METERS,
): { rows: AddressRow[]; errors: string[] } {
    const h = headerRow(rows);
    if (h < 0)
        return {
            rows: [],
            errors: ['The file needs a street address column, and a zip code or a city and state.'],
        };
    const cols = addressColumns(rows[h].map((x) => String(x ?? '')))!;
    const out: AddressRow[] = [];
    const errors: string[] = [];
    const seen = new Set<string>();
    rows.slice(h + 1).forEach((r, i) => {
        const cell = (c: number) => (c >= 0 ? String(r[c] ?? '').trim() : '');
        const rowNo = h + i + 2;
        if (r.every((x) => !String(x ?? '').trim())) return;
        const street = cell(cols.street);
        const zip = cell(cols.zip).match(/\d{5}/)?.[0] ?? '';
        const city = cell(cols.city);
        const state = cell(cols.state);
        if (!street || (!zip && !(city && state))) {
            errors.push(`Row ${rowNo}: needs a street address and a zip code or a city and state.`);
            return;
        }
        const key = [street, city, state, zip].join('|').toLowerCase();
        if (seen.has(key)) return;
        seen.add(key);
        const type = cell(cols.type) || undefined;
        const radius = Number(cell(cols.radius));
        out.push({
            row: rowNo,
            street,
            city,
            state,
            zip,
            ...(cell(cols.name) ? { name: cell(cols.name) } : {}),
            radiusMeters:
                Number.isFinite(radius) && radius >= 10 && radius <= 1000
                    ? Math.round(radius)
                    : type
                      ? radiusForType(type)
                      : defaultRadius,
            ...(type ? { type } : {}),
        });
    });
    return { rows: out, errors };
}

/** An address as a person reads it: "123 Main St, Dallas, TX 75201". */
export function addressWords(a: Pick<AddressRow, 'street' | 'city' | 'state' | 'zip'>): string {
    const place = [a.city, [a.state, a.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
    return [a.street, place].filter(Boolean).join(', ');
}
